import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, MessageCircle, Trash2, Undo2 } from "lucide-react";
import { useRecentWallPosts } from "../../lib/adminQueues";
import { BulkConfirmDialog } from "./BulkConfirmDialog";

const STATE_LABEL = {
  visible: null,
  flagged: { text: "Reported", cls: "bg-scarlet/12 text-scarlet" },
  dismissed: { text: "Flag cleared", cls: "bg-grass/15 text-grass" },
  removed: { text: "Removed", cls: "bg-ink/10 text-ink/70" },
};

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Every recent wall post, any state, with one-click remove / restore. */
export const RecentWallPosts = () => {
  const { items, loading, error, refresh, remove, restore } = useRecentWallPosts(50);
  const [pending, setPending] = useState(null); // message to confirm removal
  const [busyId, setBusyId] = useState(null);
  const highlight = useMemo(() => new URLSearchParams(window.location.search).get("msg"), []);

  useEffect(() => {
    if (!highlight || loading) return;
    document.getElementById(`wall-post-${highlight}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight, loading]);

  const doRemove = async () => {
    const m = pending;
    setBusyId(m.id);
    try {
      await remove(m.id);
      toast.success("Post removed from the wall.", { description: "Undo any time from this list." });
    } finally {
      setBusyId(null);
      setPending(null);
    }
  };

  const doRestore = async (m) => {
    setBusyId(m.id);
    try {
      await restore(m.id);
      toast.success("Post is back on the wall.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restore that post.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section data-testid="recent-wall-posts">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h3 className="font-serif text-2xl leading-tight text-ink">Recent posts on every wall</h3>
          <p className="mt-1 text-sm text-olive">Newest first, including student thank-you notes. Remove anything that doesn't belong; nothing is sent to the author.</p>
        </div>
        <button type="button" onClick={refresh} data-testid="recent-wall-refresh" className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink">
          Refresh
        </button>
      </div>
      {error && <p className="mb-4 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-olive"><Loader2 className="h-5 w-5 animate-spin" /> Loading posts…</div>
      ) : items.length === 0 ? (
        <p data-testid="recent-wall-empty" className="rounded-xl border border-dashed border-border bg-bone p-6 text-sm text-olive">No one has posted on a wall yet.</p>
      ) : (
        <ul className="divide-y divide-ink/10 rounded-xl border border-border bg-bone">
          {items.map((m) => {
            const badge = STATE_LABEL[m.state];
            const removed = m.state === "removed";
            const lit = m.id === highlight;
            return (
              <li
                key={m.id}
                id={`wall-post-${m.id}`}
                data-testid={`wall-post-${m.id}`}
                className={`scroll-mt-28 flex items-start gap-4 px-5 py-4 ${lit ? "bg-yellow/15" : ""} ${removed ? "opacity-60" : ""}`}
              >
                <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${m.kind === "thank_you" ? "bg-grass/15 text-grass" : "bg-cobalt/12 text-cobalt"}`}>
                  <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                    <span className="font-semibold text-ink">{m.author_name}</span>
                    <span className="text-olive">on</span>
                    {m.campaign_slug ? (
                      <a href={`/${m.campaign_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 italic text-ink underline-offset-2 hover:underline">
                        {m.campaign_title} <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="italic text-ink">{m.campaign_title}</span>
                    )}
                    <span className="text-[12px] text-olive">· {when(m.posted_at)}</span>
                    {badge && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${badge.cls}`}>{badge.text}</span>}
                    {m.kind === "thank_you" && <span className="rounded-full bg-grass/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-grass">Student thank-you</span>}
                  </p>
                  <p className={`mt-1.5 text-[15px] leading-relaxed text-ink ${removed ? "line-through" : ""}`} data-testid={`wall-post-text-${m.id}`}>
                    {m.message}
                  </p>
                  {(m.matched_words?.length > 0 || m.flag_reason) && (
                    <p className="mt-1.5 flex flex-wrap gap-1.5">
                      {m.flag_reason && <span className="rounded bg-scarlet/10 px-1.5 py-0.5 text-[11px] text-scarlet">Reported: {m.flag_reason}</span>}
                      {m.matched_words?.map((w) => (
                        <span key={w} className="rounded bg-yellow/40 px-1.5 py-0.5 font-mono text-[11px] text-ink">{w}</span>
                      ))}
                    </p>
                  )}
                </div>
                {removed ? (
                  <button
                    type="button"
                    onClick={() => doRestore(m)}
                    disabled={busyId === m.id}
                    data-testid={`wall-post-restore-${m.id}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink/20 px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-sand disabled:opacity-50"
                  >
                    <Undo2 className="h-3.5 w-3.5" /> Restore
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPending(m)}
                    disabled={busyId === m.id}
                    data-testid={`wall-post-remove-${m.id}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-scarlet/40 px-3 py-1.5 text-xs font-semibold text-scarlet transition-colors hover:bg-scarlet hover:text-sand disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <BulkConfirmDialog
        open={!!pending}
        onOpenChange={(v) => !v && setPending(null)}
        title="Remove this post from the wall?"
        description="It disappears from the public wall right away. The author isn't notified, and you can restore it from this list."
        itemsSummary={pending && <blockquote className="rounded-md border-l-4 border-scarlet bg-scarlet/[0.04] px-4 py-2 text-sm italic text-ink">&ldquo;{pending.message}&rdquo;</blockquote>}
        confirmLabel="Remove from wall"
        confirmTone="danger"
        onConfirm={doRemove}
      />
    </section>
  );
};
